/**
 * Resolve a user-supplied URL to an embeddable form for the LinkViewer.
 *
 * Most arbitrary websites set `X-Frame-Options: DENY` or strict CSP and
 * cannot be iframed - so we only attempt embedding for known platforms
 * that allow it (YouTube, Vimeo). Everything else falls back to the
 * "Open in new tab" card.
 */

interface EmbedInfo {
  embedUrl: string;
  provider: 'youtube' | 'vimeo';
  /** Aspect ratio hint for the wrapper - videos render best at 16:9. */
  aspect: 'video';
}

const YOUTUBE_VIDEO_ID_RE = /[a-zA-Z0-9_-]{11}/;

function parseYoutubeId(url: URL): string | null {
  const host = url.hostname.replace(/^www\.|^m\./, '');
  // youtu.be/<id>
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0];
    return YOUTUBE_VIDEO_ID_RE.test(id) ? id : null;
  }
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    // /watch?v=<id>
    const v = url.searchParams.get('v');
    if (v && YOUTUBE_VIDEO_ID_RE.test(v)) return v;
    // /embed/<id>, /shorts/<id>, /v/<id>, /live/<id>
    const m = url.pathname.match(
      /^\/(?:embed|shorts|v|live)\/([a-zA-Z0-9_-]{11})/
    );
    if (m) return m[1];
  }
  return null;
}

function parseVimeoId(url: URL): string | null {
  const host = url.hostname.replace(/^www\./, '');
  if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null;
  const m = url.pathname.match(/(\d{6,})/);
  return m ? m[1] : null;
}

export function resolveEmbed(rawUrl: string): EmbedInfo | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  const yt = parseYoutubeId(parsed);
  if (yt) {
    return {
      provider: 'youtube',
      // youtube-nocookie keeps the embed clean of analytics until the
      // user actually plays. `rel=0` suppresses unrelated suggestions.
      embedUrl: `https://www.youtube-nocookie.com/embed/${yt}?rel=0`,
      aspect: 'video',
    };
  }

  const vm = parseVimeoId(parsed);
  if (vm) {
    return {
      provider: 'vimeo',
      embedUrl: `https://player.vimeo.com/video/${vm}`,
      aspect: 'video',
    };
  }

  return null;
}
